<template>
  <div class="viewport-stack">
    <div class="render-container" ref="localContainerRef" :style="renderContainerStyle">
      <div v-if="!isViewportRendererReady" class="loading-placeholder">
        <span>正在初始化 3D 视口...</span>
      </div>
    </div>
    <slot />
    <div v-if="drawingHint" class="drawing-hint">
      <span>{{ drawingHint }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, type CSSProperties } from 'vue';
import type { Subscription } from 'rxjs';
import {
  CadThemeMode,
  CadThemeSource,
  CadThemeViewportBackgroundColor,
  ThemeEventName,
  useDispatchEvent,
  ViewportCursorStyle,
  type ThemeChangedEventPayload,
} from '@puppycad/cad-core';
import { useViewer } from '@/hooks/useViewer';
import { useGlobalConfigStore } from '@/stores';

interface EditorViewportCanvasProps {
  /** 视口容器就绪回调。 */
  onContainerReady?: (element: HTMLElement | null) => void;
  /** 视口渲染器是否已就绪。 */
  isViewportRendererReady: boolean;
  /** 底部绘图提示文案。 */
  drawingHint: string | null;
}

const props = defineProps<EditorViewportCanvasProps>();
const localContainerRef = ref<HTMLElement | null>(null);
const { getViewer } = useViewer();
const globalConfigStore = useGlobalConfigStore();

/** 当前生效的光标样式，默认为空（使用系统默认）。 */
const cursorStyle = ref<string | null>(null);

/** cursorManager 订阅句柄。 */
let cursorSubscription: Subscription | null = null;

/** 旋转模式光标资源路径。 */
const ROTATE_CURSOR_URL = '/cursors/assembly-rotate.svg';

//------ 主题视口背景 开始------
/**
 * 解析当前主题来源。
 *
 * @param theme 当前全局主题配置。
 * @returns CAD 核心可消费的主题来源枚举。
 */
function resolveThemeSource(theme: IConfig.Theme): CadThemeSource {
  switch (theme) {
    case CadThemeSource.DarkTheme:
      // 暗色主题由用户显式选择，事件 payload 保留原始来源。
      return CadThemeSource.DarkTheme;
    case CadThemeSource.OsTheme:
      // 跟随系统主题时，mode 使用实际解析结果，source 保留 osTheme。
      return CadThemeSource.OsTheme;
    case CadThemeSource.LightTheme:
    default:
      // 未知值按全局默认亮色主题处理，避免视口背景进入不确定状态。
      return CadThemeSource.LightTheme;
  }
}

/** 当前解析后的视口是否为暗色主题。 */
const isDarkViewportTheme = computed<boolean>(() => Boolean(globalConfigStore.getProviderTheme()));

/** 当前视口主题状态。 */
const viewportThemeState = computed<ThemeChangedEventPayload>(() => {
  const isDarkTheme = isDarkViewportTheme.value;

  return {
    mode: isDarkTheme ? CadThemeMode.Dark : CadThemeMode.Light,
    source: resolveThemeSource(globalConfigStore.theme),
    viewportBackgroundColor: isDarkTheme ? CadThemeViewportBackgroundColor.Dark : CadThemeViewportBackgroundColor.Light,
  };
});

/** render-container 的内联样式。 */
const renderContainerStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    backgroundColor: viewportThemeState.value.viewportBackgroundColor,
  };

  if (cursorStyle.value) {
    // 光标属于视口交互态，和背景色合并到同一个 style，避免模板里出现多处分支。
    style.cursor = cursorStyle.value;
  }

  return style;
});
//------ 主题视口背景 结束------

/**
 * 将 cad-core 光标枚举映射为浏览器可识别的 cursor 值。
 *
 * @param cursor 光标枚举值。
 * @returns CSS cursor 值；默认值返回 null 以使用系统默认。
 */
function resolveCursorStyle(cursor: ViewportCursorStyle): string | null {
  if (cursor === ViewportCursorStyle.Default) {
    return null;
  }

  if (cursor === ViewportCursorStyle.Crosshair) {
    // 旋转模式使用自定义旋转图标；若资源加载失败回退到系统 crosshair。
    return `url(${ROTATE_CURSOR_URL}) 16 16, crosshair`;
  }

  return cursor;
}

/**
 * 订阅 viewer.cursorManager 的光标变化事件。
 *
 * 设计说明：
 * 1. Viewer 在 isViewportRendererReady 为 true 后才完全可用；
 * 2. 通过监听该标志位来驱动订阅，避免 viewer 为 null 时漏订阅；
 * 3. 组件卸载时统一取消订阅防止内存泄漏。
 */
function subscribeCursorManager(): void {
  cursorSubscription?.unsubscribe();
  cursorSubscription = null;
  const viewer = getViewer();
  if (!viewer) {
    cursorStyle.value = null;
    return;
  }
  cursorSubscription = viewer.cursorManager.observeCursorChange().subscribe((event) => {
    cursorStyle.value = resolveCursorStyle(event.cursor);
  });
}

// Viewer 就绪后才能拿到 cursorManager，监听就绪标志驱动订阅。
watch(() => props.isViewportRendererReady, (ready) => {
  if (ready) {
    subscribeCursorManager();
  } else {
    cursorSubscription?.unsubscribe();
    cursorSubscription = null;
    cursorStyle.value = null;
  }
});

//------ 主题变化事件 开始------
watch(viewportThemeState, (payload) => {
  // 主题解析结果变化后立即广播，cad-core 内部模块可只依赖事件协议而不依赖 Vue store。
  useDispatchEvent(ThemeEventName.ThemeChanged, payload);
}, { immediate: true });
//------ 主题变化事件 结束------

watch(localContainerRef, (element) => {
  props.onContainerReady?.(element);
});

onMounted(() => {
  props.onContainerReady?.(localContainerRef.value);
});

onUnmounted(() => {
  props.onContainerReady?.(null);
  cursorSubscription?.unsubscribe();
  cursorSubscription = null;
});
</script>

<style scoped>
.viewport-stack {
  position: relative;
  width: 100%;
  height: 100%;
}

.render-container {
  width: 100%;
  height: 100%;
  position: relative;
  /*------ 主题视口背景 开始------*/
  background: #ffffff;
  /*------ 主题视口背景 结束------*/
}

.loading-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
}

.drawing-hint {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20, 24, 32, 0.95);
  padding: 10px 20px;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(74, 158, 255, 0.3);
  color: #a3c5ff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 9;
}
</style>
